import type { FC } from 'react';
import { Doughnut } from 'react-chartjs-2';
import type { DeviceBreakdown } from './licenseBreakdownData';

export type DeviceTypeBreakdownChartProps = {
  breakdown: DeviceBreakdown;
  /** Unit of the centre figure, e.g. `devices`. */
  unit: string;
};

const CHART_HEIGHT = 260;

/** Doughnut of licensed devices per type, with the category total in the middle. */
export const DeviceTypeBreakdownChart: FC<DeviceTypeBreakdownChartProps> = ({
  breakdown,
  unit,
}) => {
  const data = {
    labels: breakdown.items.map((item) => item.label),
    datasets: [
      {
        data: breakdown.items.map((item) => item.count),
        backgroundColor: breakdown.items.map((item) => item.color),
        borderWidth: 0,
      },
    ],
  };

  const options = {
    cutoutPercentage: 62,
    maintainAspectRatio: false,
    responsive: true,
    legend: { display: false },
    animation: { duration: 0 },
    tooltips: {
      callbacks: {
        label: (
          item: { index: number },
          chartData: { labels: string[]; datasets: Array<{ data: number[] }> },
        ) => {
          const count = chartData.datasets[0]?.data[item.index] ?? 0;
          const proportion = breakdown.items[item.index]?.proportion ?? 0;
          return ` ${chartData.labels[item.index]}: ${count.toLocaleString()} (${Math.round(proportion)}%)`;
        },
      },
    },
  };

  return (
    // `min-w-0` lets the box shrink below the canvas's current width, which is what makes chart.js
    // notice the container got smaller; `overflow-hidden` keeps the canvas inside the card until it
    // has redrawn.
    <div
      className='relative min-w-0 overflow-hidden'
      style={{ height: CHART_HEIGHT }}
    >
      <Doughnut data={data} options={options} />
      {/* Centre label as DOM rather than canvas text: no font/colour handling, and it stays
          selectable and readable by assistive tech. */}
      <div className='pointer-events-none absolute inset-0 flex flex-col items-center justify-center'>
        <span className='font-semibold text-default text-level-4'>
          {breakdown.total.toLocaleString()}
        </span>
        <span className='text-plain text-sm'>{unit}</span>
      </div>
    </div>
  );
};
